import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { KeyDetectionReportRecord } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import {
	KeyDetectionReportDialogComponent,
	KeyDetectionReportFilterOptions,
	KeyDetectionReportFilters,
} from '../key-detection-report-dialog/key-detection-report-dialog.component';
import { UserProfileService } from '../../../services/user-profile.service';
import { AppNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { DataSetTableComponent } from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DatePipe } from '@angular/common';
import { ShortTimezonePipe } from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-key-detection-report-table',
	templateUrl: './key-detection-report-table.component.html',
	styleUrls: ['./key-detection-report-table.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		DataSetComponent_1,
		BreadcrumbLabelDirective,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		ShortTimezonePipe,
		DataSetLoadingRowComponent,
	],
})
export class KeyDetectionReportTableComponent
	implements AfterViewInit, OnDestroy
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns: string[] = [
		'deviceSerialNumber',
		'deviceOwnerCompanyName',
		'keyDetectionStatus',
		'accessedByUserName',
		'deviceLocation',
		'propertyName',
		'visitEndDate',
	];
	dataSource = new MatTableDataSource<KeyDetectionReportRecord>();
	private filterSubscription?: Subscription;

	generateKeyDetectionReportLoadingKey =
		ApiLoadingKeys.generateKeyDetectionReport;
	generateKeyDetectionReportCsvLoadingKey =
		ApiLoadingKeys.generateKeyDetectionReportCsv;
	finishedLoadingInitData: boolean = false;
	isCareCompany = false;

	reportFilters: KeyDetectionReportFilters;
	compiledFilters: QueryParam[] = [];
	hasFilters = false;
	filterOptions?: KeyDetectionReportFilterOptions;

	breadcrumbLabel = $localize`:@@keyDetectionReportTable-breadcrumbLabel:Key Detection Report`;

	constructor(
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService
	) {
		this.reportFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.reportFilters;
		this.compiledFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.compiledFilters;
		this.hasFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.hasFilters;
		this.filterOptions =
			this.router.lastSuccessfulNavigation?.extras?.state?.filterOptions;
	}

	ngAfterViewInit(): void {
		this.dataSet.applyFilters();
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.generateKeyDetectionReport([
						...filter,
						...(this.compiledFilters ?? []),
					]);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});

		this.checkCareCompany()
			.then(() => {
				if (this.isCareCompany) {
					this.displayedColumns.splice(5, 0, 'clientName', 'clientIdn');
				}
			})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		if (activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(activeCompanyId);
			this.isCareCompany = companyData?.company?.companyType === 'care';
		}
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	onRefineFiltersClicked(): void {
		this.dialog
			.open(KeyDetectionReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: {
					reportFilters: this.reportFilters,
					filterOptions: this.filterOptions,
				},
			})
			.afterClosed()
			.subscribe((_: any) => {
				this.dataSet.applyFilters();
			});
	}

	async onDownloadClicked(): Promise<void> {
		const fileName = this.hasFilters
			? 'filteredKeyDetectionReport'
			: 'keyDetectionReport';
		await this.apiService.generateKeyDetectionReportCsv(
			fileName,
			this.compiledFilters
		);
	}

	rowId(row: KeyDetectionReportRecord): string {
		return row?.id || 'unknown-id';
	}
}
